/* ==========================================================================
   AURIQUE — Scroll-Scrubbed Hero engine
   Vanilla JS, no dependencies. Safe to enqueue as-is in WordPress.

   HOW IT WORKS
   1. aurique-preload.js preloads all frame images for BOTH the day-cream and
      night-cream animations under one shared loading bar (#ahLoader) and
      fires "aurique:frames-ready" once everything has arrived. This file
      just listens for that event and grabs the day-cream frames from it.
   2. The .ah-scroll-wrap section is made tall (frames * PX_PER_FRAME + 1 viewport).
   3. .ah-sticky pins itself to the viewport while that tall section scrolls past.
   4. On every scroll tick we work out how far through .ah-scroll-wrap we are (0→1),
      map that to a frame index, and draw that frame on the canvas.
   5. When the wrapper finishes scrolling past, the sticky element un-pins itself
      naturally (plain CSS `position: sticky`) and the page continues scrolling
      down into whatever section follows — no extra JS needed for that handoff.
   ========================================================================== */

(function () {
  "use strict";

  var CONFIG = {
    frameCount: 100,
    pxPerFrameDesktop: 30,
    pxPerFrameMobile: 20,       // Smooth scrub distance on mobile touchscreens
    mobileBreakpoint: 768
  };

  var root = document.getElementById("aurique-hero-root");
  if (!root) return;

  var wrap = document.getElementById("ahScrollWrap");
  var canvas = document.getElementById("ahCanvas");
  var poster = document.getElementById("ahPoster");
  var ctx = canvas.getContext("2d");
  var railFill = document.getElementById("ahRailFill");
  var railLabels = Array.prototype.slice.call(document.querySelectorAll(".ah-rail-labels li"));
  var scrollCue = document.getElementById("ahScrollCue");
  var scenes = Array.prototype.slice.call(document.querySelectorAll(".ah-scene"));

  // Scene boundaries as *frame indices* (0-based) for 100 frames
  /* [REVERT NOTE: Previously [0, 18, 42, 68, 88, CONFIG.frameCount] for 5 scenes (Reveal, Unveil, Formula, Texture, Glow).
     Unveil scene removed per user request, leaving 4 scenes:
     0: Reveal / Hydra Glow (0-42)
     1: Formula (42-68)
     2: Texture (68-88)
     3: Glow (88-100)
     To revert: set SCENE_BOUNDS = [0, 18, 42, 68, 88, CONFIG.frameCount] and uncomment Unveil in HTML/Liquid.] */
  var SCENE_BOUNDS = [0, 42, 68, 88, CONFIG.frameCount];

  var images = null;
  var maxLoadedIndex = -1;
  var lastDrawnIndex = -1;
  var ticking = false;
  var wrapHeight = 0;
  var canvasCssW = 0, canvasCssH = 0, dpr = 1;
  var currentScene = -1;

  /* ---------------- Frames arrive from the shared preloader ---------------- */
  function onFramesReady(loadedImages) {
    images = loadedImages;
    maxLoadedIndex = CONFIG.frameCount - 1;
    window.addEventListener("scroll", onScroll, { passive: true });
    var pageWrapper = document.querySelector('.page-wrapper');
    if (pageWrapper) {
      pageWrapper.addEventListener("scroll", onScroll, { passive: true });
    }
    window.addEventListener("resize", onResize);
    onResize();
    onScroll();
    drawFrame(getDesiredIndex(), true);
    if (poster) poster.classList.add("is-hidden");
  }

  window.addEventListener("aurique:frames-ready", function (e) {
    onFramesReady(e.detail.dayImages);
  });

  if (window.__auriqueFrames && window.__auriqueFrames.isReady) {
    onFramesReady(window.__auriqueFrames.dayImages);
  }

  /* ---------------- Canvas sizing ---------------- */
  function resizeCanvas() {
    var newCssW = canvas.parentElement.clientWidth;
    var newCssH = canvas.parentElement.clientHeight;
    // Prevent canvas context reset/flicker on mobile when URL bar collapses during scroll
    if (canvasCssW > 0 && Math.abs(newCssW - canvasCssW) < 2 && Math.abs(newCssH - canvasCssH) < 25) {
      return;
    }
    canvasCssW = newCssW;
    canvasCssH = newCssH;
    dpr = Math.min(window.devicePixelRatio || 1, 2);
    canvas.width = Math.round(canvasCssW * dpr);
    canvas.height = Math.round(canvasCssH * dpr);
    canvas.style.width = canvasCssW + "px";
    canvas.style.height = canvasCssH + "px";
    ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
    lastDrawnIndex = -1; // force redraw at new size
  }

  function setWrapHeight() {
    var isMobile = window.innerWidth < CONFIG.mobileBreakpoint;
    var pxPerFrame = isMobile ? CONFIG.pxPerFrameMobile : CONFIG.pxPerFrameDesktop;
    wrapHeight = CONFIG.frameCount * pxPerFrame + window.innerHeight;
    wrap.style.height = wrapHeight + "px";
  }

  function onResize() {
    setWrapHeight();
    resizeCanvas();
    drawFrame(lastDrawnIndex >= 0 ? getDesiredIndex() : 0, true);
  }

  /* ---------------- Draw ---------------- */
  function drawFrame(index, force) {
    if (!images) return;
    index = Math.max(0, Math.min(CONFIG.frameCount - 1, index));
    var safeIndex = Math.min(index, Math.max(maxLoadedIndex, 0));
    if (!force && safeIndex === lastDrawnIndex) return;
    var img = images[safeIndex];
    if (!img || !img.naturalWidth) return;

    var iw = img.naturalWidth, ih = img.naturalHeight;
    /* 100% Cover scale to fill canvas edge-to-edge with zero side gaps */
    var scale = Math.max(canvasCssW / iw, canvasCssH / ih);
    var dw = iw * scale, dh = ih * scale;
    var dx = (canvasCssW - dw) / 2;
    var isMobile = canvasCssW < CONFIG.mobileBreakpoint;
    var dy = isMobile ? (canvasCssH - dh) * 0.18 : (canvasCssH - dh) / 2;

    ctx.clearRect(0, 0, canvasCssW, canvasCssH);
    ctx.drawImage(img, dx, dy, dw, dh);
    lastDrawnIndex = safeIndex;
  }

  /* ---------------- Scroll → progress → frame ---------------- */
  function getProgress() {
    var rect = wrap.getBoundingClientRect();
    var scrolled = -rect.top;
    var range = wrapHeight - window.innerHeight;
    if (range <= 0) return 0;
    var p = scrolled / range;
    return Math.max(0, Math.min(1, p));
  }

  function getDesiredIndex() {
    var p = getProgress();
    return Math.round(p * (CONFIG.frameCount - 1));
  }

  function sceneForIndex(idx) {
    for (var s = 0; s < SCENE_BOUNDS.length - 1; s++) {
      if (idx >= SCENE_BOUNDS[s] && idx < SCENE_BOUNDS[s + 1]) return s;
    }
    return SCENE_BOUNDS.length - 2;
  }

  function updateScenes(idx, progress) {
    var scene = sceneForIndex(idx);
    if (scene !== currentScene) {
      currentScene = scene;
      scenes.forEach(function (el, i) {
        el.classList.toggle("is-active", i === scene);
      });
      railLabels.forEach(function (el, i) {
        el.classList.toggle("is-active", i === scene);
      });
    }
        
    if (railFill) railFill.style.height = (progress * 100) + "%";
    if (scrollCue) scrollCue.classList.toggle("is-hidden", progress > 0.03);
  }

  function onScroll() {
    if (ticking) return;
    ticking = true;
    requestAnimationFrame(function () {
      var idx = getDesiredIndex();
      var progress = getProgress();
      drawFrame(idx);
      updateScenes(idx, progress);
      ticking = false;
    });
  }

  /* ---------------- Init ---------------- */
  setWrapHeight();
  resizeCanvas();
  window.addEventListener("resize", setWrapHeight);
})();
